import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import DirectorDemoMode from "@/components/DirectorDemoMode";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import { ForgotPasswordPage, ResetPasswordPage, ActivatePage } from "@/pages/AuthFlowPages";
import AuthCallback from "@/pages/AuthCallback";
import InschrijfPage from "@/pages/InschrijfPage";
import PublicAanmeldPage from "@/pages/PublicAanmeldPage";
import AanbodPage from "@/pages/AanbodPage";
import ContractPortalPage from "@/pages/ContractPortalPage";
import OuderStatusPage from "@/pages/OuderStatusPage";
import AanmeldingenPage from "@/pages/AanmeldingenPage";
import DashboardPage from "@/pages/DashboardPage";
import DirectiePage from "@/pages/DirectiePage";
import KinderenPage from "@/pages/KinderenPage";
import KindTimelinePage from "@/pages/KindTimelinePage";
import OudersPage from "@/pages/OudersPage";
import ContractenPage from "@/pages/ContractenPage";
import MedewerkersPage from "@/pages/MedewerkersPage";
import PlanningPage from "@/pages/PlanningPage";
import ActiviteitenPage from "@/pages/ActiviteitenPage";
import OnboardingPage from "@/pages/OnboardingPage";
import OnboardingStartPage from "@/pages/OnboardingStartPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import { PlatformOverviewPage, PlatformOrgDetailPage } from "@/pages/PlatformOverviewPage";
import PlatformShell from "@/components/PlatformShell";
import PlatformAuditPage from "@/pages/PlatformAuditPage";
import PlatformAanmeldlinksPage from "@/pages/PlatformAanmeldlinksPage";
import PlatformNewOrganizationPage from "@/pages/PlatformNewOrganizationPage";
import { getImpersonatedOrg } from "@/lib/api";
import UitnodigingenPage from "@/pages/UitnodigingenPage";
import OnboardingWizardPage from "@/pages/OnboardingWizardPage";
import PublicOnboardingPage from "@/pages/PublicOnboardingPage";
import MigratiePage from "@/pages/MigratiePage";
import GebruikersPage from "@/pages/GebruikersPage";
import FactureringPage from "@/pages/FactureringPage";
import DebiteurenPage from "@/pages/DebiteurenPage";
import AuditPage from "@/pages/AuditPage";
import InstellingenPage from "@/pages/InstellingenPage";
import BKRInstellingenPage from "@/pages/BKRInstellingenPage";
import BKRVergelijkPage from "@/pages/BKRVergelijkPage";
import IncidentenPage from "@/pages/IncidentenPage";
import MedicatiePage from "@/pages/MedicatiePage";
import BKRTimelinePage from "@/pages/BKRTimelinePage";
import AdminBeveiligingPage from "@/pages/AdminBeveiligingPage";
import InspectiepakketPage from "@/pages/InspectiepakketPage";
import InklokPage from "@/pages/InklokPage";
import RegisterPage from "@/pages/RegisterPage";
import WachtlijstPage from "@/pages/WachtlijstPage";
import CommunicatiePage from "@/pages/CommunicatiePage";
import SalarisPage from "@/pages/SalarisPage";
import WebhooksPage from "@/pages/WebhooksPage";
import RapportagesPage from "@/pages/RapportagesPage";
import OuderPortaalPage from "@/pages/OuderPortaalPage";
import GroepsappPage from "@/pages/GroepsappPage";
import RoosterPage from "@/pages/RoosterPage";
import MijnRoosterPage from "@/pages/MijnRoosterPage";
import MobileBottomNav from "@/components/MobileBottomNav";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import PwaUpdateBanner from "@/components/PwaUpdateBanner";
import OfflineStatusPill from "@/components/OfflineStatusPill";
import PortaalMeerPage from "@/pages/PortaalMeerPage";
import PortaalAlbumPage from "@/pages/PortaalAlbumPage";
import PortaalWeekplanPage from "@/pages/PortaalWeekplanPage";
import PortaalPortfolioPage from "@/pages/PortaalPortfolioPage";
import PortaalDagverslagPage from "@/pages/PortaalDagverslagPage";
import PortaalBerichtenPage from "@/pages/PortaalBerichtenPage";
import PortaalPlanningPage from "@/pages/PortaalPlanningPage";
import PortaalFacturenPage from "@/pages/PortaalFacturenPage";
import PortaalWeekstoryPage from "@/pages/PortaalWeekstoryPage";
import GroupDashboardPage from "@/pages/GroupDashboardPage";
import PersonalStaffDashboardPage from "@/pages/PersonalStaffDashboardPage";
import GroupAccountsAdminPage from "@/pages/GroupAccountsAdminPage";
import OrganisatieInstellingenPage from "@/pages/OrganisatieInstellingenPage";

function Shell({ children }) {
  const { user } = useAuth();
  // Ouder-role is auto-redirected naar ouderportaal
  if (user?.role === "ouder") return <Navigate to="/portaal" replace />;
  // Platform-super zonder actieve supportmodus → forceer /platform (mag geen
  // KDV-omgeving zien tot bewust een organisatie is geselecteerd)
  const isPlatformSuper = user?.role === "platform_admin" ||
    (user?.role === "superadmin" && !user?.organization_id);
  if (isPlatformSuper && !getImpersonatedOrg()) {
    return <Navigate to="/platform" replace />;
  }
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

/** Route guard: eist expliciet dat de user in een van `allowed` rollen zit,
 * anders redirect naar /app. Voorkomt dat een verborgen nav-item toch via
 * directe URL toegankelijk is (deep-link bypass). Wacht op auth-hydration
 * om race-redirects te voorkomen. */
function RoleShell({ children, allowed }) {
  const { user, status } = useAuth();
  if (status === "loading") return <div className="min-h-screen flex items-center justify-center">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (!user) return null;
  const isPlatformSuper = user.role === "platform_admin" ||
    (user.role === "superadmin" && !user.organization_id);
  const ok = allowed.includes(user.role) || (isPlatformSuper && getImpersonatedOrg());
  if (!ok) return <Navigate to="/app" replace />;
  return <Shell>{children}</Shell>;
}

/** Alleen platform-super mag deze legacy KDV-routes benaderen. Anders
 * redirect naar /platform (voor plat-super buiten supportmodus) of /app. */
function PlatformOnlyShell({ children }) {
  const { user, status } = useAuth();
  if (status === "loading") return <div className="min-h-screen flex items-center justify-center">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (!user) return null;
  const isPlatformSuper = user.role === "platform_admin" ||
    (user.role === "superadmin" && !user.organization_id);
  if (!isPlatformSuper) return <Navigate to="/app" replace />;
  if (!getImpersonatedOrg()) return <Navigate to="/platform" replace />;
  return <Shell>{children}</Shell>;
}

/** Platform-only shell, geen KDV-navigatie. Alleen voor platform-super. */
function PlatformShellGuard({ children }) {
  const { user, status } = useAuth();
  // Wacht op auth-hydration voordat we routing beslissen; anders faalt een
  // full-load van /platform/organisaties/:id omdat user momenteel null is
  // (race die iter80 aantoonde: pa-view CTA verloor het pad).
  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Laden…</div>;
  }
  if (status === "anon") return <Navigate to="/login" replace />;
  const isPlatformSuper = user?.role === "platform_admin" ||
    (user?.role === "superadmin" && !user?.organization_id);
  if (!isPlatformSuper) {
    return <Navigate to="/app" replace />;
  }
  return (
    <ProtectedRoute>
      <PlatformShell>{children}</PlatformShell>
    </ProtectedRoute>
  );
}

/** /app root voor pedagogisch → doorsturen naar Presentielijst (dashboard is niet toegankelijk). */
function AppHome() {
  const { user } = useAuth();
  if (user?.role === "groep_account") return <Navigate to="/app/groep-dashboard" replace />;
  if (user?.role === "pedagogisch_medewerker") return <Navigate to="/app/medewerker-dashboard" replace />;
  if (user?.role === "pedagogisch") return <Navigate to="/app/planning" replace />;
  // Platform-super zonder supportmodus mag GEEN KDV-dashboard zien.
  const isPlatformSuper = user?.role === "platform_admin" ||
    (user?.role === "superadmin" && !user?.organization_id);
  if (isPlatformSuper && !getImpersonatedOrg()) return <Navigate to="/platform" replace />;
  return <Shell><DashboardPage /></Shell>;
}

/** Rol-guard voor management-only routes zoals /app/directie.
 * Pedagogisch/anderen worden expliciet geredirect naar /app in plaats van
 * een half-gebroken cockpit te zien. */
const _DIRECTIE_ROLES = new Set(["superadmin", "platform_admin", "locatiemanager", "directeur", "financieel"]);
function DirectieGuard() {
  const { user } = useAuth();
  if (user && !_DIRECTIE_ROLES.has(user.role)) {
    if (user.role === "ouder") return <Navigate to="/portaal" replace />;
    return <Navigate to="/app" replace />;
  }
  return <Shell><DirectiePage /></Shell>;
}

function HomeRedirect() {
  const { user, status } = useAuth();
  if (status === "loading") return <div className="min-h-screen flex items-center justify-center">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  // Ouder → portaal, pedagogisch (leidster) → Presentielijst (Groepsapp via nav)
  if (user?.role === "ouder") return <Navigate to="/portaal" replace />;
  if (user?.role === "pedagogisch") return <Navigate to="/app/planning" replace />;
  // Platform-super zonder supportmodus → platform-dashboard (nooit KDV-app)
  const isPlatformSuper = user?.role === "platform_admin" ||
    (user?.role === "superadmin" && !user?.organization_id);
  if (isPlatformSuper && !getImpersonatedOrg()) return <Navigate to="/platform" replace />;
  return <Navigate to="/app" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <NotificationsProvider>
          <BrowserRouter>
            <AppRouter />
            <DirectorDemoMode />
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </NotificationsProvider>
      </AuthProvider>
    </div>
  );
}

/** Synchronously routes Emergent OAuth callbacks (session_id in URL fragment)
 *  to AuthCallback BEFORE normal routes/guards. Prevents 401 race conditions. */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/activate" element={<ActivatePage />} />
        <Route path="/onboarding-start" element={<OnboardingStartPage />} />
        <Route path="/inschrijven" element={<InschrijfPage />} />
        <Route path="/aanmelden/:slug" element={<PublicAanmeldPage />} />
        <Route path="/aanbod/:token" element={<AanbodPage />} />
        <Route path="/contract/:token" element={<ContractPortalPage />} />
        <Route path="/status/:token" element={<OuderStatusPage />} />
        <Route path="/portaal" element={<ProtectedRoute><OuderPortaalPage /></ProtectedRoute>} />
        <Route path="/portaal/meer" element={<ProtectedRoute><PortaalMeerPage /></ProtectedRoute>} />
        <Route path="/portaal/album" element={<ProtectedRoute><PortaalAlbumPage /></ProtectedRoute>} />
        <Route path="/portaal/weekplan" element={<ProtectedRoute><PortaalWeekplanPage /></ProtectedRoute>} />
        <Route path="/portaal/portfolio" element={<ProtectedRoute><PortaalPortfolioPage /></ProtectedRoute>} />
        <Route path="/portaal/dagverslag" element={<ProtectedRoute><PortaalDagverslagPage /></ProtectedRoute>} />
        <Route path="/portaal/berichten" element={<ProtectedRoute><PortaalBerichtenPage /></ProtectedRoute>} />
        <Route path="/portaal/planning" element={<ProtectedRoute><PortaalPlanningPage /></ProtectedRoute>} />
        <Route path="/portaal/facturen" element={<ProtectedRoute><PortaalFacturenPage /></ProtectedRoute>} />
        <Route path="/portaal/weekstory" element={<ProtectedRoute><PortaalWeekstoryPage /></ProtectedRoute>} />
        <Route path="/groep" element={<ProtectedRoute><GroepsappPage /></ProtectedRoute>} />
        {/* Platform-superadmin dashboard — buiten /app zodat platform-super
             nooit per ongeluk KDV-shell binnen komt. */}
        <Route path="/platform" element={<PlatformShellGuard><PlatformOverviewPage /></PlatformShellGuard>} />
        <Route path="/platform/organisaties" element={<PlatformShellGuard><PlatformOverviewPage /></PlatformShellGuard>} />
        <Route path="/platform/organisaties/nieuw" element={<PlatformShellGuard><PlatformNewOrganizationPage /></PlatformShellGuard>} />
        <Route path="/platform/organisaties/:organizationId" element={<PlatformShellGuard><PlatformOrgDetailPage /></PlatformShellGuard>} />
        <Route path="/platform/onboardingen" element={<PlatformShellGuard><PlatformAdminPage /></PlatformShellGuard>} />
        <Route path="/platform/gebruikers" element={<PlatformShellGuard><PlatformOverviewPage /></PlatformShellGuard>} />
        <Route path="/platform/audit" element={<PlatformShellGuard><PlatformAuditPage /></PlatformShellGuard>} />
        <Route path="/platform/aanmeldlinks" element={<PlatformShellGuard><PlatformAanmeldlinksPage /></PlatformShellGuard>} />
        <Route path="/app" element={<AppHome />} />
        {/* Nieuwe rol-dashboards */}
        <Route path="/app/groep-dashboard" element={<ProtectedRoute><GroupDashboardPage /></ProtectedRoute>} />
        <Route path="/app/medewerker-dashboard" element={<ProtectedRoute><PersonalStaffDashboardPage /></ProtectedRoute>} />
        <Route path="/app/groepsaccounts" element={<RoleShell allowed={["superadmin","platform_admin","directeur","beheerder","locatiemanager"]}><GroupAccountsAdminPage /></RoleShell>} />
        <Route path="/app/organisatie-instellingen" element={<RoleShell allowed={["superadmin","platform_admin","directeur","beheerder"]}><OrganisatieInstellingenPage /></RoleShell>} />
        <Route path="/app/kinderen" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","administratie","financieel"]}><KinderenPage /></RoleShell>} />
        <Route path="/app/kinderen/:kindId/timeline" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","administratie","pedagogisch","pedagogisch_medewerker","groep_account","ouder"]}><KindTimelinePage /></RoleShell>} />
        <Route path="/app/directie" element={<DirectieGuard />} />
        <Route path="/app/ouders" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","administratie","financieel"]}><OudersPage /></RoleShell>} />
        <Route path="/app/contracten" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","administratie","financieel"]}><ContractenPage /></RoleShell>} />
        <Route path="/app/medewerkers" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","administratie"]}><MedewerkersPage /></RoleShell>} />
        <Route path="/app/wachtlijst" element={<RoleShell allowed={["superadmin","platform_admin","directeur","administratie"]}><WachtlijstPage /></RoleShell>} />
        <Route path="/app/aanmeldingen" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager"]}><AanmeldingenPage /></RoleShell>} />
        <Route path="/app/planning" element={<RoleShell allowed={["superadmin","platform_admin","directeur","beheerder","locatiemanager","pedagogisch","groep_account","administratie","financieel"]}><PlanningPage /></RoleShell>} />
        <Route path="/app/activiteiten" element={<RoleShell allowed={["superadmin","platform_admin","directeur","beheerder","locatiemanager","pedagogisch","groep_account"]}><ActiviteitenPage /></RoleShell>} />
        <Route path="/app/rooster" element={<RoleShell allowed={["superadmin","platform_admin","directeur","beheerder","locatiemanager","administratie"]}><RoosterPage /></RoleShell>} />
        <Route path="/app/mijn-rooster" element={<RoleShell allowed={["superadmin","platform_admin","directeur","beheerder","locatiemanager","pedagogisch","pedagogisch_medewerker"]}><MijnRoosterPage /></RoleShell>} />
        <Route path="/app/communicatie" element={<Shell><CommunicatiePage /></Shell>} />
        <Route path="/app/facturering" element={<RoleShell allowed={["superadmin","platform_admin","directeur","financieel","administratie"]}><FactureringPage /></RoleShell>} />
        <Route path="/app/salaris" element={<RoleShell allowed={["superadmin","platform_admin","directeur"]}><SalarisPage /></RoleShell>} />
        <Route path="/app/debiteuren" element={<RoleShell allowed={["superadmin","platform_admin","directeur","financieel","administratie"]}><DebiteurenPage /></RoleShell>} />
        <Route path="/app/webhooks" element={<Shell><WebhooksPage /></Shell>} />
        <Route path="/app/rapportages" element={<RoleShell allowed={["superadmin","platform_admin"]}><RapportagesPage /></RoleShell>} />
        <Route path="/app/audit" element={<Shell><AuditPage /></Shell>} />
        <Route path="/app/instellingen" element={<RoleShell allowed={["superadmin","platform_admin","directeur"]}><InstellingenPage /></RoleShell>} />
        <Route path="/app/bkr-instellingen" element={<RoleShell allowed={["superadmin","platform_admin","directeur","beheerder","locatiemanager"]}><BKRInstellingenPage /></RoleShell>} />
        <Route path="/app/bkr-vergelijk" element={<RoleShell allowed={["superadmin","platform_admin","locatiemanager"]}><BKRVergelijkPage /></RoleShell>} />
        <Route path="/app/incidenten" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","pedagogisch","groep_account","financieel"]}><IncidentenPage /></RoleShell>} />
        <Route path="/app/medicatie" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","pedagogisch","groep_account"]}><MedicatiePage /></RoleShell>} />
        <Route path="/app/bkr-timeline" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager"]}><BKRTimelinePage /></RoleShell>} />
        <Route path="/app/beveiliging" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager"]}><AdminBeveiligingPage /></RoleShell>} />
        <Route path="/app/inspectiepakket" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager"]}><InspectiepakketPage /></RoleShell>} />
        <Route path="/app/inklok" element={<RoleShell allowed={["superadmin","platform_admin","directeur","locatiemanager","pedagogisch","pedagogisch_medewerker","financieel","administratie"]}><InklokPage /></RoleShell>} />
        <Route path="/portaal/medicatie" element={<ProtectedRoute><MedicatiePage /></ProtectedRoute>} />
        <Route path="/portaal/medicatie/:mid" element={<ProtectedRoute><MedicatiePage /></ProtectedRoute>} />
        <Route path="/app/onboarding" element={<PlatformOnlyShell><OnboardingPage /></PlatformOnlyShell>} />
        <Route path="/app/platform" element={<PlatformOnlyShell><PlatformAdminPage /></PlatformOnlyShell>} />
        <Route path="/app/uitnodigingen" element={<PlatformOnlyShell><UitnodigingenPage /></PlatformOnlyShell>} />
        <Route path="/app/onboarding-wizard" element={<RoleShell allowed={["superadmin","platform_admin","directeur"]}><OnboardingWizardPage /></RoleShell>} />
        <Route path="/app/gebruikers" element={<Shell><GebruikersPage /></Shell>} />
        <Route path="/onboarding/:token" element={<PublicOnboardingPage />} />
        <Route path="/onboarding-start" element={<Shell><OnboardingWizardPage /></Shell>} />
        <Route path="/app/migratie" element={<RoleShell allowed={["superadmin","platform_admin"]}><MigratiePage /></RoleShell>} />
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
      {/* PWA globale UI: SW-update banner, install-prompt en mobiele bottom-nav */}
      <PwaUpdateBanner />
      <PwaInstallPrompt />
      <OfflineStatusPill />
      <MobileBottomNav />
    </>
  );
}

export default App;
